import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { PessoasService } from '../../../services/pessoa/pessoas.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';

export interface ItemResumoDTO {
  id: number;
  data: string;
}

export interface ConsultaMedicaResumoDTO {
  id: number;
  dataAgendamento: string | null;
  observacoes?: string | null;
  status?: 'Não realizada' | 'Realizada' | string | null;
}

export interface AtividadePropostaResumoDTO {
  idPessoa: number;
  habitoAlimentar: ItemResumoDTO | null;
  planoAlimentar: ItemResumoDTO | null;
  consultaMedica: ConsultaMedicaResumoDTO | null;
  observacoesAtividadeProposta?: string | null;
}

@Component({
  selector: 'app-atividade-proposta',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './atividade-proposta.html',
  styleUrl: './atividade-proposta.scss',
})
export class AtividadeProposta implements OnInit {
  constructor(
    private pessoasService: PessoasService,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
  ) {}

  carregando = false;
  salvando = false;

  pessoaId!: number;
  resumo: AtividadePropostaResumoDTO | null = null;

  consultaMarcada = false;
  consultaData: Date | null = null;

  consultaObservacoes = '';
  private consultaObservacoesOriginal = '';

  outrasInformacoes = '';

  erro = signal('');

  async ngOnInit(): Promise<void> {
    await this.buscarResumo();
  }

  private toApiDate(d: Date): string {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  private parseApiDate(value: string): Date | null {
    if (!value) return null;

    if (value.includes('/')) {
      const [dia, mes, ano] = value.split('/');
      const d = new Date(+ano, +mes - 1, +dia);
      return isNaN(d.getTime()) ? null : d;
    }

    const iso = value.slice(0, 10);
    const d = new Date(iso + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
  }

  async buscarResumo(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) return;

    this.pessoaId = +id;
    this.carregando = true;
    this.erro.set('');

    try {
      this.resumo = await this.pessoasService.buscarResumoAtividadeProposta(this.pessoaId);
      this.outrasInformacoes = this.resumo?.observacoesAtividadeProposta ?? '';
      
      const c = this.resumo?.consultaMedica;

      this.consultaObservacoesOriginal = c?.observacoes ?? '';
      this.consultaObservacoes = this.consultaObservacoesOriginal;

      if (c?.dataAgendamento) {
        this.consultaMarcada = true;
        this.consultaData = this.parseApiDate(c.dataAgendamento);
      } else {
        this.consultaMarcada = false;
        this.consultaData = null;
      }


    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao buscar resumo';
      this.erro.set(msg);
      this.snackbarService.error(msg);
    } finally {
      this.carregando = false;
    }
  }

  onToggleConsulta(checked: boolean): void {
    this.consultaMarcada = checked;

    if (!checked) {
      this.consultaData = null;
      return;
    }

    if (!this.consultaData) {
      this.consultaData = new Date();
    }
  }

  cancelar(): void {
    this.consultaObservacoes = this.consultaObservacoesOriginal;

    const c = this.resumo?.consultaMedica;
    if (c?.dataAgendamento) {
      this.consultaData = this.parseApiDate(c.dataAgendamento);
      this.consultaMarcada = true;
    } else {
      this.consultaData = null;
      this.consultaMarcada = false;
    }
  }

  // Observação é OPCIONAL, mas se preencher: 15–300
  private validarObservacoesSeInformada(obs: string): boolean {
    const txt = (obs ?? '').trim();
    if (txt.length === 0) return true;

    if (txt.length < 15 || txt.length > 300) {
      this.snackbarService.error('Observações deve ter entre 15 e 300 caracteres.');
      return false;
    }
    return true;
  }

  async salvar(): Promise<void> {
  const outras = (this.outrasInformacoes ?? '').trim();
  if (!this.validarObservacoesSeInformada(outras)) return;

  const obs = (this.consultaObservacoes ?? '').trim();
  if (!this.validarObservacoesSeInformada(obs)) return;

  const payload: any = {
    idPessoa: this.pessoaId,
    observacoesAtividadeProposta: outras.length ? outras : null,

    //SEMPRE manda consultaMedica para salvar observações mesmo sem marcar
    consultaMedica: {
      status: this.consultaMarcada ? 'Realizada' : 'Não realizada',
      observacoes: obs.length ? obs : null,
      dataAgendamento: this.consultaMarcada
        ? this.toApiDate(this.consultaData ?? new Date())
        : null,
    },
  };

  this.salvando = true;
  try {
    await this.pessoasService.criarConsultaMedica(this.pessoaId, payload);

    this.snackbarService.success('Dados salvos com sucesso!');
    await this.buscarResumo();
  } catch (err: any) {
    const msg = err?.response?.data?.message || err?.message || 'Erro ao salvar';
    this.snackbarService.error(msg);
  } finally {
    this.salvando = false;
  }
}

}
